import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import {
  collection, doc, getDocs, getDoc, setDoc, writeBatch, addDoc,
  query, where, serverTimestamp, deleteDoc, updateDoc, arrayRemove,
} from "firebase/firestore";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

/* =========================================================================
   Class Mapping Panel — admin-only
   Workflow:
     1. Admin downloads a template (studentId, name, email, batch, classCode)
     2. Admin uploads the filled Excel — students are written to studentRoster
        with their classCode.
     3. The panel then lists every unique classCode with its student count.
        Admin picks a teacher email for each classCode and clicks "Create".
     4. A facultyClasses doc is created/updated per (classCode, teacher) —
        studentEmails auto-populated from the roster.
   Result: the teacher logs in → 📚 My Classroom → their class is ready with
   every mapped student already in the roster.
   ========================================================================= */

export default function ClassMappingPanel({ user }) {
  const [roster, setRoster] = useState([]);
  const [classCodes, setClassCodes] = useState([]);  // [{ code, count, batches: Set, students: [], studentDocs: [], existingClassId }]
  const [teachers, setTeachers] = useState([]);
  const [teacherAssign, setTeacherAssign] = useState({}); // classCode → teacherEmail
  const [classNames, setClassNames] = useState({});       // classCode → friendly name
  const [expanded, setExpanded] = useState({});           // classCode → bool
  const [studentFilter, setStudentFilter] = useState({}); // classCode → search string
  const [loading, setLoading] = useState(true);
  const [uploadingRoster, setUploadingRoster] = useState(false);
  const [creating, setCreating] = useState(false);
  const [rosterFile, setRosterFile] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);

  useEffect(() => { reloadAll(); }, []);

  const reloadAll = async () => {
    setLoading(true);
    try {
      // Roster
      const rosterSnap = await getDocs(collection(db, "studentRoster"));
      const allStudents = rosterSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setRoster(allStudents);

      // Group by classCode
      const map = new Map();
      allStudents.forEach(s => {
        const code = (s.classCode || "").trim();
        if (!code) return;
        if (!map.has(code)) map.set(code, { code, count: 0, levels: new Set(), students: [], studentDocs: [] });
        const e = map.get(code);
        e.count++;
        if (s.level) e.levels.add(s.level);
        else if (s.batch) e.levels.add(s.batch);   // backward compat for old data
        e.students.push(s.email);
        e.studentDocs.push(s);
      });

      // Existing faculty classes by classCode
      const fcSnap = await getDocs(collection(db, "facultyClasses"));
      const existingByCode = {};
      fcSnap.docs.forEach(d => {
        const data = d.data();
        if (data.classCode) {
          existingByCode[data.classCode] = { id: d.id, facultyEmail: data.facultyEmail, name: data.name };
        }
      });

      // Build display list and pre-populate teacherAssign / classNames
      const codeList = Array.from(map.values()).sort((a, b) => a.code.localeCompare(b.code));
      const nextAssign = { ...teacherAssign };
      const nextNames = { ...classNames };
      codeList.forEach(c => {
        c.existing = existingByCode[c.code];
        if (!nextAssign[c.code]) nextAssign[c.code] = c.existing?.facultyEmail || "";
        if (!nextNames[c.code]) nextNames[c.code] = c.existing?.name || c.code;
      });
      setClassCodes(codeList);
      setTeacherAssign(nextAssign);
      setClassNames(nextNames);

      // Teachers (anyone with role teacher or admin)
      const rolesSnap = await getDocs(collection(db, "userRoles"));
      setTeachers(rolesSnap.docs.map(d => ({ email: d.id, ...d.data() }))
                                 .filter(t => t.role === "teacher" || t.role === "admin")
                                 .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email)));
    } catch (e) {
      console.error("Reload failed", e);
      alert("❌ Load failed: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  /* ---------- Excel template download ---------- */
  const downloadTemplate = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Students");
    ws.columns = [
      { header: "Student ID",   key: "studentId",    width: 14 },
      { header: "Name",         key: "name",         width: 24 },
      { header: "Email",        key: "email",        width: 32 },
      { header: "Level",        key: "level",        width: 22 },
      { header: "Class Code",   key: "classCode",    width: 18 },
      { header: "Teacher Email",key: "teacherEmail", width: 30 },
      { header: "Teacher Name", key: "teacherName",  width: 22 },
    ];
    ws.getRow(1).eachCell(c => {
      c.font = { bold: true, color: { argb: "FFFFFFFF" } };
      c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1A237E" } };
      c.alignment = { horizontal: "center" };
    });
    // Add a small instruction sheet
    const helpWs = wb.addWorksheet("Allowed values");
    helpWs.addRow(["Level (column D)"]);
    helpWs.addRow(["PUC-I"]);
    helpWs.addRow(["PUC-II"]);
    helpWs.addRow(["Engineering Physics"]);
    helpWs.addRow([]);
    helpWs.addRow(["Class Code (column E) — your own naming scheme:"]);
    helpWs.addRow(["PUC-I  : AB-I-001, AB-I-002, AB-I-003, ..."]);
    helpWs.addRow(["PUC-II : AB-II-001, AB-II-002, ..."]);
    helpWs.addRow(["Engineering Physics : ECE-C1, ECE-C2, CSE-C1, ME-C1, EEE-C3, ..."]);
    helpWs.getColumn(1).width = 70;

    // PUC-I sample rows
    ws.addRow({ studentId: "R200001", name: "Ravi Kumar",   email: "r200001@rgukt.ac.in", level: "PUC-I",  classCode: "AB-I-001",  teacherEmail: "suresh@rgukt.ac.in", teacherName: "Prof. Suresh" });
    ws.addRow({ studentId: "R200002", name: "Priya Sharma", email: "r200002@rgukt.ac.in", level: "PUC-I",  classCode: "AB-I-001",  teacherEmail: "suresh@rgukt.ac.in", teacherName: "Prof. Suresh" });
    // PUC-II sample row
    ws.addRow({ studentId: "R200003", name: "Ajay Reddy",   email: "r200003@rgukt.ac.in", level: "PUC-II", classCode: "AB-II-002", teacherEmail: "deepa@rgukt.ac.in",  teacherName: "Prof. Deepa"  });
    // Engineering Physics sample rows
    ws.addRow({ studentId: "R190101", name: "Vamsi Krishna",email: "r190101@rgukt.ac.in", level: "Engineering Physics", classCode: "ECE-C1", teacherEmail: "rama@rgukt.ac.in",  teacherName: "Prof. Rama"  });
    ws.addRow({ studentId: "R190201", name: "Ananya Joshi", email: "r190201@rgukt.ac.in", level: "Engineering Physics", classCode: "CSE-C2", teacherEmail: "vijay@rgukt.ac.in", teacherName: "Prof. Vijay" });
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "Roster_Full_Template.xlsx");
  };

  /* ---------- File picker + preview ---------- */
  const onFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setRosterFile(f);
    setPreviewRows([]);
    const reader = new FileReader();
    reader.onload = async (evt) => {
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(evt.target.result);
      const ws = wb.worksheets[0];
      const rows = [];
      ws.eachRow((row, rowNum) => {
        if (rowNum === 1) return;
        const studentId    = String(row.getCell(1).value || "").trim();
        const name         = String(row.getCell(2).value || "").trim();
        const email        = String(row.getCell(3).value || "").trim().toLowerCase();
        const level        = String(row.getCell(4).value || "").trim();   // PUC-I / PUC-II / Engineering Physics
        const classCode    = String(row.getCell(5).value || "").trim();
        const teacherEmail = String(row.getCell(6).value || "").trim().toLowerCase();
        const teacherName  = String(row.getCell(7).value || "").trim();
        if (email) rows.push({ studentId, name, email, level, classCode, teacherEmail, teacherName });
      });
      setPreviewRows(rows);
    };
    reader.readAsArrayBuffer(f);
  };

  /* ---------- One-shot upload: roster + teachers + classes ---------- */
  const uploadRoster = async () => {
    if (previewRows.length === 0) { alert("Pick a file first."); return; }

    // Strip teacher columns from what goes into studentRoster (they're class-level, not student-level)
    const studentRows = previewRows.map(({ teacherEmail, teacherName, ...rest }) => rest);

    // Group by (classCode, teacherEmail) — these become facultyClasses
    const classGroups = new Map(); // key: classCode|teacherEmail
    const teacherMap = new Map();  // email -> name
    previewRows.forEach(r => {
      if (r.teacherEmail) teacherMap.set(r.teacherEmail, r.teacherName || r.teacherEmail);
      if (!r.classCode) return;
      // If no teacherEmail is provided for a class row, skip auto-class-create (admin can pick later)
      if (!r.teacherEmail) return;
      const key = `${r.classCode}||${r.teacherEmail}`;
      if (!classGroups.has(key)) classGroups.set(key, {
        classCode: r.classCode, teacherEmail: r.teacherEmail, teacherName: r.teacherName,
        students: [], levels: new Set(),
      });
      const g = classGroups.get(key);
      g.students.push(r.email);
      if (r.level) g.levels.add(r.level);
    });

    const summary = `📋 Will upload:
 • ${previewRows.length} students to master roster
 • ${teacherMap.size} teacher(s) auto-promoted to role=teacher
 • ${classGroups.size} class(es) created / updated (one per Class Code + Teacher pair)

Proceed?`;
    if (!window.confirm(summary)) return;

    setUploadingRoster(true);
    try {
      /* 1️⃣  Master roster */
      for (let i = 0; i < studentRows.length; i += 500) {
        const chunk = studentRows.slice(i, i + 500);
        const batch = writeBatch(db);
        chunk.forEach(s => batch.set(doc(db, "studentRoster", s.email), s, { merge: true }));
        await batch.commit();
      }

      /* 2️⃣  Auto-promote teachers in userRoles */
      if (teacherMap.size > 0) {
        const batch = writeBatch(db);
        for (const [tEmail, tName] of teacherMap.entries()) {
          batch.set(doc(db, "userRoles", tEmail), {
            email: tEmail, name: tName, role: "teacher",
            createdAt: serverTimestamp(),
          }, { merge: true });
        }
        await batch.commit();
      }

      /* 3️⃣  Look up existing facultyClasses keyed by (classCode + facultyEmail) so we update instead of duplicate */
      const fcSnap = await getDocs(collection(db, "facultyClasses"));
      const existing = {};
      fcSnap.docs.forEach(d => {
        const data = d.data();
        if (data.classCode && data.facultyEmail) existing[`${data.classCode}||${data.facultyEmail}`] = d.id;
      });

      /* 4️⃣  Create / update facultyClasses */
      let created = 0, updated = 0;
      for (const [, g] of classGroups) {
        const lvls = Array.from(g.levels);
        const inferredLevel = lvls.length === 1 ? lvls[0] : null;   // mixed-level class → null
        const payload = {
          facultyEmail: g.teacherEmail,
          facultyName: g.teacherName || g.teacherEmail,
          name: g.classCode,
          subject: "Physics",
          icon: inferredLevel === "Engineering Physics" ? "⚙️" : inferredLevel === "PUC-II" ? "📗" : "📘",
          level: inferredLevel,
          classCode: g.classCode,
          studentEmails: g.students,
          scheduleSlots: 7,
          periodTimes: {
            1: "9:00 – 10:00", 2: "10:00 – 11:00", 3: "11:00 – 12:00",
            4: "1:30 – 2:30",  5: "2:30 – 3:30",   6: "3:30 – 4:30", 7: "4:30 – 5:30",
          },
          updatedAt: serverTimestamp(),
        };
        const key = `${g.classCode}||${g.teacherEmail}`;
        if (existing[key]) {
          await setDoc(doc(db, "facultyClasses", existing[key]), payload, { merge: true });
          updated++;
        } else {
          await addDoc(collection(db, "facultyClasses"), { ...payload, createdAt: serverTimestamp() });
          created++;
        }
      }

      alert(`✅ Done.
 • ${studentRows.length} students in master roster
 • ${teacherMap.size} teacher(s) promoted
 • ${created} class(es) created, ${updated} updated`);

      setRosterFile(null); setPreviewRows([]);
      await reloadAll();
    } catch (e) {
      alert("❌ Upload failed: " + e.message);
    } finally {
      setUploadingRoster(false);
    }
  };

  /* ---------- Create / update faculty classes for each classCode ---------- */
  const createClasses = async () => {
    const pending = classCodes.filter(c => teacherAssign[c.code]);
    if (pending.length === 0) { alert("Assign at least one teacher first."); return; }
    if (!window.confirm(`Create / update ${pending.length} class(es) and map their students automatically?`)) return;
    setCreating(true);
    let created = 0, updated = 0;
    try {
      for (const c of pending) {
        const teacherEmail = teacherAssign[c.code];
        const teacher = teachers.find(t => t.email === teacherEmail);
        const friendlyName = (classNames[c.code] || c.code).trim();
        const lvls = Array.from(c.levels || []);
        const inferredLevel = lvls.length === 1 ? lvls[0] : null;
        const payload = {
          facultyEmail: teacherEmail,
          facultyName: teacher?.name || teacherEmail,
          name: friendlyName,
          subject: "Physics",
          icon: inferredLevel === "Engineering Physics" ? "⚙️" : inferredLevel === "PUC-II" ? "📗" : "📘",
          level: inferredLevel,
          classCode: c.code,
          studentEmails: c.students.map(e => (e || "").toLowerCase()),
          scheduleSlots: 7,
          periodTimes: {
            1: "9:00 – 10:00", 2: "10:00 – 11:00", 3: "11:00 – 12:00",
            4: "1:30 – 2:30",  5: "2:30 – 3:30",   6: "3:30 – 4:30", 7: "4:30 – 5:30",
          },
          updatedAt: serverTimestamp(),
        };

        if (c.existing) {
          await setDoc(doc(db, "facultyClasses", c.existing.id), payload, { merge: true });
          updated++;
        } else {
          await addDoc(collection(db, "facultyClasses"), { ...payload, createdAt: serverTimestamp() });
          created++;
        }
      }
      alert(`✅ Done.\n${created} class(es) created.\n${updated} class(es) updated with latest student list.`);
      await reloadAll();
    } catch (e) {
      alert("❌ Create failed: " + e.message);
    } finally {
      setCreating(false);
    }
  };

  /* ---------- Quick sync only (push roster -> existing class) ---------- */
  const resyncOne = async (c) => {
    if (!c.existing) { alert("No class created for this code yet."); return; }
    if (!window.confirm(`Re-sync ${c.students.length} students into "${c.existing.name}"?`)) return;
    try {
      await setDoc(doc(db, "facultyClasses", c.existing.id),
        { studentEmails: c.students.map(e => (e || "").toLowerCase()), updatedAt: serverTimestamp() },
        { merge: true });
      alert(`✅ Roster synced.`);
      reloadAll();
    } catch (e) { alert("❌ " + e.message); }
  };

  /* ---------- Delete a single student (from master roster + all classes) ---------- */
  const deleteStudent = async (s) => {
    if (!window.confirm(`Permanently delete this student?\n\n${s.name || ""}\n${s.email}\n\nRemoves them from the master roster AND every class roster.\nQuiz results stay (for audit).`)) return;
    try {
      // 1. Remove from any facultyClasses studentEmails arrays
      const fcSnap = await getDocs(collection(db, "facultyClasses"));
      for (const d of fcSnap.docs) {
        if ((d.data().studentEmails || []).includes(s.email)) {
          await updateDoc(d.ref, { studentEmails: arrayRemove(s.email), updatedAt: serverTimestamp() });
        }
      }
      // 2. Delete from studentRoster
      await deleteDoc(doc(db, "studentRoster", s.email));
      reloadAll();
    } catch (e) { alert("❌ " + e.message); }
  };

  /* ---------- Delete an entire class code (its students + the faculty class doc) ---------- */
  const deleteClassCode = async (c) => {
    const msg = `⚠️ Permanently delete the entire class "${c.code}"?\n\nThis will:\n • Delete ${c.count} students from the master roster\n • Delete the faculty class itself (and its schedule, reading materials, quizzes, discussion posts will remain orphaned)\n\nTo only unlink the teacher without deleting students, use the ✕ button in the Manage Teachers section above instead.\n\nProceed?`;
    if (!window.confirm(msg)) return;
    try {
      // 1. Delete the faculty class doc if it exists
      if (c.existing) await deleteDoc(doc(db, "facultyClasses", c.existing.id));
      // 2. Delete every student with this classCode from studentRoster
      for (const s of c.studentDocs) {
        await deleteDoc(doc(db, "studentRoster", s.email));
      }
      reloadAll();
    } catch (e) { alert("❌ " + e.message); }
  };

  /* ─────────────────────────── Render ─────────────────────────── */
  return (
    <div style={{ padding: "10px 0" }}>
      <h2 style={{ color: "#1a237e", margin: "0 0 4px" }}>📚 Class Mapping</h2>
      <p style={{ color: "#37474f", fontWeight: 500, margin: "0 0 18px", fontSize: "0.92rem" }}>
        <strong>One-shot setup:</strong> upload an Excel with <em>Level</em> (PUC-I / PUC-II / Engineering Physics),
        <em> Class Code</em> (e.g. AB-I-001, ECE-C1), <em>Teacher Email</em> and <em>Teacher Name</em> — the system creates every class,
        promotes every teacher, and maps every student in a single click.
        You can also leave teacher columns blank and assign manually in the table below.
      </p>

      {/* === Manage Teachers — without this the teacher dropdown stays empty === */}
      <ManageTeachers teachers={teachers} onChange={reloadAll} />

      {/* === 1. Excel upload === */}
      <Section title="① Upload Student Roster (with Class Code)" accent="#1565c0">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 12 }}>
          <button onClick={downloadTemplate} style={btnSecondary}>📥 Download Template (.xlsx)</button>
          <input type="file" accept=".xlsx,.xls" onChange={onFileChange} style={{ flex: 1, minWidth: 220 }} />
          <button onClick={uploadRoster} disabled={uploadingRoster || previewRows.length === 0} style={btnPrimary}>
            {uploadingRoster ? "⏳ Uploading…" : `⬆ Upload ${previewRows.length || ""} Row(s)`}
          </button>
        </div>

        {previewRows.length > 0 && (
          <div style={{ background: "#fafbff", border: "1px solid #c5cae9", borderRadius: 8, padding: 10, maxHeight: 200, overflowY: "auto" }}>
            <div style={{ fontSize: "0.82rem", color: "#1a237e", fontWeight: 700, marginBottom: 6 }}>
              Preview · {previewRows.length} row(s)
            </div>
            <table style={{ width: "100%", fontSize: "0.8rem", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#e8eaf6" }}>
                  <th style={tdth}>SID</th><th style={tdth}>Name</th><th style={tdth}>Email</th>
                  <th style={tdth}>Level</th><th style={tdth}>Class Code</th>
                  <th style={tdth}>Teacher Email</th><th style={tdth}>Teacher Name</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.slice(0, 50).map((r, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #eef0f7" }}>
                    <td style={tdth}>{r.studentId}</td>
                    <td style={tdth}>{r.name}</td>
                    <td style={tdth}>{r.email}</td>
                    <td style={tdth}>{r.level || r.batch || ""}</td>
                    <td style={tdth}><strong>{r.classCode || <span style={{ color: "#c62828" }}>— missing —</span>}</strong></td>
                    <td style={tdth}>{r.teacherEmail || <span style={{ color: "#c62828" }}>—</span>}</td>
                    <td style={tdth}>{r.teacherName || <span style={{ color: "#999" }}>—</span>}</td>
                  </tr>
                ))}
                {previewRows.length > 50 && (
                  <tr><td colSpan={7} style={{ ...tdth, color: "#546e7a", textAlign: "center", padding: 8 }}>… +{previewRows.length - 50} more rows …</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* === 2. Map classCodes → teachers === */}
      <Section title="② Map Class Codes to Teachers" accent="#2e7d32">
        {loading ? <p style={{ color: "#546e7a" }}>⏳ Loading…</p>
          : classCodes.length === 0 ? (
            <p style={{ color: "#37474f", fontWeight: 500 }}>
              No class codes in the roster yet. Upload a roster above that includes the <strong>Class Code</strong> column.
            </p>
          ) : (
            <>
              <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #c5cae9" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
                  <thead>
                    <tr style={{ background: "#1a237e", color: "#fff" }}>
                      <th style={th}>Class Code</th>
                      <th style={th}>Class Name (rename)</th>
                      <th style={{ ...th, textAlign: "center" }}>Students</th>
                      <th style={{ ...th, textAlign: "center" }}>Level</th>
                      <th style={th}>Assigned Teacher</th>
                      <th style={th}>Status</th>
                      <th style={th}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {classCodes.map((c, i) => {
                      const isOpen = !!expanded[c.code];
                      const filter = (studentFilter[c.code] || "").toLowerCase();
                      const studentList = c.studentDocs.filter(s =>
                        !filter || (s.name || "").toLowerCase().includes(filter) ||
                                   (s.email || "").toLowerCase().includes(filter) ||
                                   (s.studentId || "").toLowerCase().includes(filter)
                      );
                      return (
                      <React.Fragment key={c.code}>
                      <tr style={{ background: i % 2 ? "#fafbff" : "#fff", borderBottom: "1px solid #eef0f7" }}>
                        <td style={td}>
                          <button onClick={() => setExpanded(prev => ({ ...prev, [c.code]: !prev[c.code] }))} style={{
                            background: "none", border: "none", cursor: "pointer", color: "#1a237e",
                            fontWeight: 700, fontSize: "0.95rem", padding: 0, marginRight: 6
                          }}>{isOpen ? "▾" : "▸"}</button>
                          <strong style={{ color: "#1a237e" }}>{c.code}</strong>
                        </td>
                        <td style={td}>
                          <input value={classNames[c.code] || ""}
                            onChange={e => setClassNames(prev => ({ ...prev, [c.code]: e.target.value }))}
                            style={{ width: "100%", padding: "5px 8px", borderRadius: 5, border: "1px solid #c5cae9" }} />
                        </td>
                        <td style={{ ...td, textAlign: "center", fontWeight: 700, color: "#1a237e" }}>{c.count}</td>
                        <td style={{ ...td, textAlign: "center" }}>
                          {Array.from(c.levels).map(lvl => {
                            const meta = LEVEL_META[lvl] || { bg: "#eceff1", color: "#37474f" };
                            return (
                              <span key={lvl} style={{
                                background: meta.bg, color: meta.color,
                                padding: "2px 8px", borderRadius: 10,
                                fontSize: "0.72rem", fontWeight: 700,
                                marginRight: 4, whiteSpace: "nowrap"
                              }}>{lvl}</span>
                            );
                          })}
                        </td>
                        <td style={td}>
                          <select value={teacherAssign[c.code] || ""}
                            onChange={e => setTeacherAssign(prev => ({ ...prev, [c.code]: e.target.value }))}
                            style={{ width: "100%", padding: "5px 8px", borderRadius: 5, border: "1px solid #c5cae9" }}>
                            <option value="">— pick teacher —</option>
                            {teachers.map(t => <option key={t.email} value={t.email}>{t.name || t.email} ({t.role})</option>)}
                          </select>
                        </td>
                        <td style={td}>
                          {c.existing ? (
                            <span style={{ background: "#e8f5e9", color: "#1b5e20", borderRadius: 10, padding: "2px 8px", fontSize: "0.74rem", fontWeight: 700 }}>
                              ✓ class created
                            </span>
                          ) : (
                            <span style={{ background: "#fff3e0", color: "#e65100", borderRadius: 10, padding: "2px 8px", fontSize: "0.74rem", fontWeight: 700 }}>
                              new
                            </span>
                          )}
                        </td>
                        <td style={td}>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {c.existing && (
                              <button onClick={() => resyncOne(c)} style={{ ...btnSecondary, padding: "5px 10px", fontSize: "0.78rem" }}>🔄 Re-sync</button>
                            )}
                            <button onClick={() => deleteClassCode(c)} style={{
                              background: "#fff", color: "#c62828", border: "1.5px solid #ef9a9a",
                              borderRadius: 6, padding: "5px 10px", fontWeight: 700, cursor: "pointer", fontSize: "0.78rem"
                            }}>🗑 Delete class</button>
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr style={{ background: "#f8fafe" }}>
                          <td colSpan={7} style={{ padding: "10px 18px 14px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
                              <div style={{ fontWeight: 700, color: "#1a237e", fontSize: "0.88rem" }}>
                                👥 Students in {c.code} <span style={{ color: "#546e7a", fontWeight: 500 }}>({studentList.length} of {c.count})</span>
                              </div>
                              <input
                                value={studentFilter[c.code] || ""}
                                onChange={e => setStudentFilter(prev => ({ ...prev, [c.code]: e.target.value }))}
                                placeholder="🔍 Search by name / email / ID"
                                style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #c5cae9", fontSize: "0.82rem", minWidth: 240 }}
                              />
                            </div>
                            {studentList.length === 0 ? (
                              <p style={{ color: "#546e7a", margin: 0, fontSize: "0.84rem" }}>No matches.</p>
                            ) : (
                              <div style={{ maxHeight: 320, overflowY: "auto", border: "1px solid #c5cae9", borderRadius: 7, background: "#fff" }}>
                                <table style={{ width: "100%", fontSize: "0.82rem", borderCollapse: "collapse" }}>
                                  <thead>
                                    <tr style={{ background: "#e8eaf6" }}>
                                      <th style={tdth}>SID</th>
                                      <th style={tdth}>Name</th>
                                      <th style={tdth}>Email</th>
                                      <th style={tdth}>Level</th>
                                      <th style={{ ...tdth, textAlign: "center" }}>Action</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {studentList.map(s => (
                                      <tr key={s.email} style={{ borderBottom: "1px solid #f0f2f9" }}>
                                        <td style={tdth}>{s.studentId || "—"}</td>
                                        <td style={tdth}><strong style={{ color: "#1a237e" }}>{s.name || "—"}</strong></td>
                                        <td style={tdth}>{s.email}</td>
                                        <td style={tdth}>{s.level || s.batch || "—"}</td>
                                        <td style={{ ...tdth, textAlign: "center" }}>
                                          <button onClick={() => deleteStudent(s)} style={{
                                            background: "#fff", color: "#c62828", border: "1.5px solid #ef9a9a",
                                            borderRadius: 5, padding: "3px 9px", fontWeight: 700, cursor: "pointer", fontSize: "0.74rem"
                                          }} title="Delete this student">🗑 Remove</button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button onClick={reloadAll} style={btnSecondary}>↻ Refresh</button>
                <button onClick={createClasses} disabled={creating} style={btnPrimary}>
                  {creating ? "⏳ Creating…" : "💾 Create / Update Classes"}
                </button>
              </div>

              <p style={{ marginTop: 10, fontSize: "0.82rem", color: "#37474f", fontWeight: 500 }}>
                Tip: Re-running this is safe. Existing classes keep their schedule / quizzes / readings — only the student list is refreshed.
              </p>
            </>
          )}
      </Section>

      {/* === 3. Quick stats === */}
      <Section title="③ Summary" accent="#6a1b9a">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
          <Stat label="Total Students" value={roster.length} color="#1565c0" bg="#e3f2fd" />
          <Stat label="Unique Class Codes" value={classCodes.length} color="#2e7d32" bg="#e8f5e9" />
          <Stat label="Mapped to Teacher" value={classCodes.filter(c => c.existing).length} color="#e65100" bg="#fff3e0" />
          <Stat label="Awaiting Mapping" value={classCodes.filter(c => !c.existing).length} color="#c62828" bg="#ffebee" />
        </div>
      </Section>
    </div>
  );
}

/* ── Manage Teachers ── */
function ManageTeachers({ teachers, onChange }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(null);

  const add = async () => {
    const e = email.trim().toLowerCase();
    const n = name.trim();
    if (!e || !e.includes("@")) { alert("Enter a valid email"); return; }
    if (!n) { alert("Enter the teacher's name"); return; }
    if (teachers.find(t => t.email === e)) { alert("Already a teacher"); return; }
    setSaving(true);
    try {
      await setDoc(doc(db, "userRoles", e), {
        role: "teacher",
        name: n,
        email: e,
        createdAt: serverTimestamp(),
      }, { merge: true });
      setEmail(""); setName("");
      onChange && onChange();
      alert(`✅ ${n} added as teacher.\nThey can now sign in with ${e} and see "📚 My Classroom" in their nav.`);
    } catch (err) { alert("❌ " + err.message); }
    finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (t.role === "admin") { alert("Cannot remove admin."); return; }
    if (!window.confirm(`Remove ${t.name || t.email} from teachers?\n(They keep their account but lose the My Classroom nav link.)`)) return;
    setRemoving(t.email);
    try {
      await setDoc(doc(db, "userRoles", t.email), { role: "student" }, { merge: true });
      onChange && onChange();
    } catch (err) { alert("❌ " + err.message); }
    finally { setRemoving(null); }
  };

  return (
    <Section title={`👨‍🏫 Teachers in System (${teachers.length})`} accent="#6a1b9a">
      <p style={{ color: "#37474f", fontWeight: 500, margin: "0 0 10px", fontSize: "0.86rem" }}>
        These are the people you can assign as faculty in the table below.
        Add a teacher here, then their email appears in the <strong>Assigned Teacher</strong> dropdown.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(140px,1fr) minmax(160px,1.5fr) auto", gap: 8, marginBottom: 12 }}>
        <input value={name}  onChange={e => setName(e.target.value)}  placeholder="Teacher's name (e.g. Prof. Suresh)" style={inp} />
        <input value={email} onChange={e => setEmail(e.target.value)} placeholder="teacher@gmail.com" style={inp} />
        <button onClick={add} disabled={saving} style={btnPrimary}>{saving ? "Adding…" : "＋ Add Teacher"}</button>
      </div>

      {teachers.length === 0 ? (
        <p style={{ color: "#c62828", fontWeight: 600, margin: 0 }}>
          ⚠️ No teachers yet — add at least one above to unlock the dropdown in the mapping table below.
        </p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 8 }}>
          {teachers.map(t => (
            <div key={t.email} style={{
              background: "#fafbff", border: "1.5px solid #c5cae9", borderRadius: 8,
              padding: "8px 12px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: "#1a237e", fontSize: "0.9rem" }}>
                  {t.name || t.email}
                  {t.role === "admin" && <span style={{ marginLeft: 6, background: "#0d1b5e", color: "#fff", borderRadius: 8, padding: "1px 7px", fontSize: "0.7rem", fontWeight: 700 }}>ADMIN</span>}
                </div>
                <div style={{ color: "#546e7a", fontSize: "0.78rem", overflow: "hidden", textOverflow: "ellipsis" }}>{t.email}</div>
              </div>
              {t.role !== "admin" && (
                <button onClick={() => remove(t)} disabled={removing === t.email} style={{
                  background: "#fff", color: "#c62828", border: "1.5px solid #ef9a9a",
                  borderRadius: 6, padding: "4px 10px", fontWeight: 700, cursor: "pointer", fontSize: "0.74rem"
                }}>{removing === t.email ? "…" : "✕"}</button>
              )}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

const inp = { padding: "8px 10px", borderRadius: 7, border: "1.5px solid #c5cae9", fontSize: "0.88rem", outline: "none" };

const LEVEL_META = {
  "PUC-I":               { bg: "#e3f2fd", color: "#1565c0" },  // blue
  "PUC-II":              { bg: "#e8f5e9", color: "#1b5e20" },  // green
  "Engineering Physics": { bg: "#f3e5f5", color: "#6a1b9a" },  // purple
  // backward-compat for old data that still uses batch C1 / C2
  "C1":                  { bg: "#e3f2fd", color: "#1565c0" },
  "C2":                  { bg: "#fff3e0", color: "#e65100" },
};

/* ── UI helpers ── */
const btnPrimary = { background: "#1a237e", color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontWeight: 700, cursor: "pointer", fontSize: "0.88rem" };
const btnSecondary = { background: "#fff", color: "#1a237e", border: "1.5px solid #c5cae9", borderRadius: 8, padding: "9px 16px", fontWeight: 700, cursor: "pointer", fontSize: "0.88rem" };
const th = { textAlign: "left", padding: "10px 12px", fontWeight: 700, fontSize: "0.82rem" };
const td = { padding: "8px 12px", verticalAlign: "middle" };
const tdth = { padding: "5px 8px", textAlign: "left", borderBottom: "1px solid #eef0f7" };

function Section({ title, accent, children }) {
  return (
    <div style={{
      background: "#fff", border: "1.5px solid #c5cae9",
      borderTop: `5px solid ${accent}`, borderRadius: 10,
      padding: 18, marginBottom: 18,
      boxShadow: "0 3px 10px rgba(26,35,126,0.08)"
    }}>
      <h3 style={{ margin: "0 0 12px", color: accent, fontWeight: 800, fontSize: "1.02rem", paddingBottom: 8, borderBottom: `2px solid ${accent}22` }}>{title}</h3>
      {children}
    </div>
  );
}

function Stat({ label, value, color, bg }) {
  return (
    <div style={{ background: bg, borderRadius: 8, padding: "12px 14px" }}>
      <div style={{ fontSize: "0.72rem", color, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      <div style={{ fontSize: "1.45rem", color, fontWeight: 800 }}>{value}</div>
    </div>
  );
}
